import React from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';
import { usePriceRowContext } from 'src/areas/product/components/price-list/price-row-context';

import {
  CaptionText,
  FontColor,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';
import { MediumUp } from 'src/ui/block';
import { ShippingLabel } from '../shipping/shipping-label';
import { getCompiledPriceData } from '../offer-price-experiment/price-data';

import {
  PriceDisplay,
  PriceDisplayEnum,
} from 'src/components/price-list/types';
import {
  CampaignMediaColors,
  Maybe,
  OfferPrices,
  Price,
  AlternativePrice,
} from 'src/types';

import {
  PriceAndShipping,
  StyledBadge,
  StyledPriceLabel,
  StyledSecondaryPrice,
  Wrapper,
} from './styles';
import { OptionTypeEnum } from 'src/components/filter-dropdown';

interface IPriceLabel {
  offer: Price | AlternativePrice;
  offerPrice?: OfferPrices | null;
  priceDisplay: PriceDisplay;
  campaignColors?: Maybe<CampaignMediaColors>;
}

export const OfferPriceLabel = (props: IPriceLabel) => {
  const { offerPrice, campaignColors, priceDisplay, offer } = props;

  const { t, market } = useAppContext();
  const { isFeatured = false, forceMobileView, sorting } = usePriceRowContext();

  if (!offer.price) {
    return null;
  }

  const priceData = getCompiledPriceData(
    offer.price,
    offerPrice,
    priceDisplay,
    isFeatured,
    t,
    market
  );

  const getPriceLabel = () => {
    let shippingCost = offer.shipping?.cheapest?.shippingCost || 0;
    const cost = offer.price?.exclShipping || 0;

    if (sorting === OptionTypeEnum.FASTEST_DELIVERY) {
      shippingCost = offer.shipping?.fastest?.shippingCost || 0;
    }

    if (priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING) {
      return cost + shippingCost;
    }

    return cost;
  };

  return (
    <Wrapper>
      {priceData.label && (
        <>
          {priceData.isMemberPrice ? (
            <StyledBadge color="GREY_INVERTED" text={priceData.label} />
          ) : (
            <CaptionText emphasis={FontEmphasis.MEDIUM}>
              {priceData.label}
            </CaptionText>
          )}
        </>
      )}

      <PriceAndShipping>
        {offer.price && isFeatured && !forceMobileView && (
          <MediumUp>
            <ShippingLabel offer={offer} priceDisplay={priceDisplay} />
          </MediumUp>
        )}

        {priceData.secondaryPrice && (
          <StyledSecondaryPrice
            value={priceData.secondaryPrice}
            emphasis={FontEmphasis.LOW}
          />
        )}

        {getPriceLabel() && (
          <StyledPriceLabel
            value={getPriceLabel()}
            emphasis={isFeatured ? FontEmphasis.HIGH : FontEmphasis.LOW}
            fontColor={campaignColors ? FontColor.NEGATIVE : undefined}
          />
        )}
      </PriceAndShipping>
    </Wrapper>
  );
};
